"use client";

/**
 * P11-20 · Tell a shopper when the currency they chose is no longer on offer.
 *
 * `resolveCurrency` has always fallen back to the base currency for a stored
 * preference the brand has since de-listed, and that fallback is correct — no
 * 500, and never a base-priced total presented as though it were the display
 * currency. What it was not is *honest*: someone who chose EUR last week comes
 * back to GBP prices with nothing on screen to say why, which from their side
 * is indistinguishable from a pricing bug.
 *
 * Choosing the current currency explicitly is the fix, not just the dismissal:
 * it writes a valid cookie through the same action the switcher uses, so the
 * stale preference is gone and the notice cannot return. A plain "dismiss"
 * would leave the dead cookie in place to nag on the next visit.
 */
import * as React from "react";

import { useMarketPicker } from "~/components/storefront/market/market-picker-context";
import { setCurrencyPreference } from "~/server/i18n/set-currency";

export function CurrencyFallbackNotice({
  rejected,
  current,
}: {
  /** The de-listed code the shopper had stored, e.g. "EUR". */
  rejected: string;
  /** What they are being shown instead — the brand's base currency. */
  current: string;
}) {
  const [dismissed, setDismissed] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(false);
  const picker = useMarketPicker();

  if (dismissed) return null;

  async function keepCurrent() {
    setPending(true);
    // Best-effort: if the write fails the notice simply returns next request,
    // which is the safe direction — better a repeated notice than a silent
    // swap.
    const result = await setCurrencyPreference(current).catch(() => null);
    setPending(false);
    if (!result?.ok) { setError(true); return; }
    setDismissed(true);
  }

  return (
    <div
      // `status`, not `alert`: informative, not urgent, and it must not
      // interrupt a screen-reader user mid-sentence.
      role="status"
      className="border-b border-border bg-surface-2 px-4 py-2 text-center text-xs text-fg-muted"
    >
      <span>
        {rejected} is no longer available on this store, so prices are shown in{" "}
        <strong className="text-fg">{current}</strong>.
      </span>{" "}
      <button
        type="button"
        onClick={keepCurrent}
        disabled={pending}
        className="underline underline-offset-2 hover:no-underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {pending ? "Saving…" : `Continue in ${current}`}
      </button>{" "}
      {picker && <button className="underline underline-offset-2" onClick={() => picker.openPicker()}>Choose another currency</button>}
      {error && <span role="alert"> Could not save your choice. Please try again.</span>}
    </div>
  );
}
